// hooks/useConfigs.js
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useSetRecoilState } from "recoil";
import configState from "../recoil/configState";

const useConfigs = () => {
  const [configs, setConfigs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const setConfigState = useSetRecoilState(configState);

  const fetchConfigs = useCallback(
    async (cancelToken) => {
      try {
        setLoading(true);
        const endpoint = `${process.env.REACT_APP_TENANTS_API_ENDPOINT}/api/v1/org`;
        const response = await axios.get(endpoint);
        const data = response.data;
        setConfigs(data);

        // Update Recoil state with the formatted data
        setConfigState({
          initialized: true,
          org_identifier: data.org_identifier,
          colors: {
            primary_color: data.theme.colors.primary_color,
            secondary_color: data.theme.colors.secondary_color,
            primary_dark_color: data.theme.colors.primary_dark_color,
            secondary_dark_color: data.theme.colors.secondary_dark_color,
            primary_gradient_color: data.theme.colors.primary_gradient_color,
            secondary_gradient_color:
              data.theme.colors.secondary_gradient_color,
          },
          meta: {
            title: data.theme.meta.title,
            description: data.theme.meta.description,
            org_name: data.theme.meta.org_name,
            tagline: data.theme.meta.tagline,
            documentation_url: data.theme.meta.documentation_url,
          },
          assets: {
            logoSrc: data.theme.assets.logo_src,
            imageSrc: data.theme.assets.image_src,
            faviconSrc: data.theme.assets.favicon_src,
            // Note: Adjust this if your API nests co_branded inside assets
            co_branded: data.theme.co_branded,
          },
        });
        setError(null);
      } catch (err) {
        if (axios.isCancel(err)) {
          console.log("Request canceled:", err.message);
        } else {
          setError(err.message || "Failed to fetch configurations");
          console.error("Error fetching configurations:", err);
        }
      } finally {
        setLoading(false);
      }
    },
    [setConfigState]
  );

  useEffect(() => {
    const source = axios.CancelToken.source();
    fetchConfigs(source.token);

    // Cleanup: cancel the request if the component unmounts
    return () => {
      source.cancel("Component unmounted, canceling request");
    };
  }, [fetchConfigs]);

  const refreshConfigs = () => {
    const source = axios.CancelToken.source();
    fetchConfigs(source.token);
  };

  return { configs, loading, error, refreshConfigs };
};

export default useConfigs;
